import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ActivityButton,
  SequenceNumberInput,
} from "../../../components/Common";
import {
  CreateSubjectNoteRequest,
  State,
  SubjectNoteDto,
} from "../../../interfaces";
import {
  addSubjectNote,
  downloadNoteFile,
  getSubjectNote,
  updateSubjectNote,
  uploadSubjectNote,
} from "../../../store/actions";
import { MediaDto } from "../../../interfaces/media";
import { PDF_ACCEPT } from "../../../constants";
import { formatFileSize } from "../../../services";
import { SequenceEnum } from "../../../enums";

type TProps = {
  id?: string | null;
  subjectId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditSubjectNoteModal: React.FC<TProps> = ({
  id,
  subjectId,
  show,
  onCloseClick,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [request] = useState<CreateSubjectNoteRequest>();
  const { loading, success, error } = useSelector(
    (state: any): State<SubjectNoteDto> => state.subjectNote.addSubjectNote
  );

  const {
    loading: updateSubjectNoteLoading,
    success: updateSubjectNoteSuccess,
    error: updateSubjectNoteError,
  } = useSelector(
    (state: any): State<SubjectNoteDto> => state.subjectNote.updateSubjectNote
  );

  const { error: getSubjectNoteError, response: subjectNote } = useSelector(
    (state: any): State<SubjectNoteDto> => state.subjectNote.subjectNote
  );

  const {
    loading: uploading,
    error: uploadError,
    success: uploadSuccess,
    response: uploadSubjectNoteResponse,
  } = useSelector(
    (state: any): State<MediaDto> => state.subjectNote.uploadSubjectNote
  );

  const {
    loading: downloading,
    success: downloadSuccess,
    error: downloadError,
    response: downloadResponse,
  } = useSelector(
    (state: any): State<any> => state.subjectNote.downloadSubjectNote
  );

  //#region form
  const form = useFormik<CreateSubjectNoteRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      fileId: "",
      sequenceNumber: request?.sequenceNumber || 0,
      subjectId: subjectId,
    },
    initialErrors: error || updateSubjectNoteError,
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            })
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" })
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            })
          ),
      }),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        })
      ),
    }),
    onSubmit: (values) => {
      if (id) {
        dispatch(
          updateSubjectNote(
            id,
            { ...values, sequenceNumber: Number(values.sequenceNumber) },
            history
          )
        );
      } else {
        dispatch(
          addSubjectNote(
            { ...values, sequenceNumber: Number(values.sequenceNumber) },
            history
          )
        );
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (
      (!loading && success) ||
      (!updateSubjectNoteLoading && updateSubjectNoteSuccess)
    ) {
      onClose();
    }
  }, [loading, updateSubjectNoteLoading]);

  useEffect(() => {
    if (!downloading && downloadSuccess && downloadResponse) {
      try {
        const blobUrl = window.URL.createObjectURL(
          new Blob([downloadResponse.data], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute(
          "download",
          subjectNote?.file.name ||
            uploadSubjectNoteResponse?.name ||
            "downloaded.pdf"
        );
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.log(error);
      }
    }
    if (!downloading && !downloadSuccess && downloadError) {
      toast.error(t("downloadingError"), {
        autoClose: 3000,
      });
    }
  }, [downloading, downloadSuccess, downloadError]);

  useEffect(() => {
    if (id) {
      dispatch(getSubjectNote(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (!uploading && uploadSuccess && uploadSubjectNoteResponse) {
      form.setFieldValue("fileId", uploadSubjectNoteResponse.id);
    }
  }, [uploading, uploadError, uploadSuccess]);

  useEffect(() => {
    if (id && subjectNote) {
      form.setValues({
        name: subjectNote.name,
        fileId: subjectNote?.file?.id || "",
        sequenceNumber:
          form.values.sequenceNumber || subjectNote?.sequenceNumber || 0,
        subjectId: subjectId,
      });
    }
  }, [subjectNote]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  //#endregion

  //#region handlers
  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const pdfFile = files[0];
      if (!pdfFile.name.match(/\.(pdf)$/)) {
        toast.error(t("selectPdfOnly"), {
          autoClose: 3000,
        });
        return false;
      }
      dispatch(uploadSubjectNote(pdfFile, id));
      event.target.value = "";
    }
  };

  const onDownload = async () => {
    try {
      if (id) dispatch(downloadNoteFile(id));
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "subjectNote" })
              : t("addItem", { item: "subjectNote" })}
          </ModalHeader>
          {typeof error === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{error}</p>
            </Alert>
          )}
          {typeof getSubjectNoteError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{getSubjectNoteError}</p>
            </Alert>
          )}
          {typeof updateSubjectNoteError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{updateSubjectNoteError}</p>
            </Alert>
          )}
          {typeof uploadError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{uploadError}</p>
            </Alert>
          )}
          <ModalBody>
            <div
              className="text-center p-4 mb-3"
              style={{ border: "solid 1px #ced4da", borderRadius: "5px" }}
            >
              <label htmlFor="subject-note-input" className="cursor-pointer">
                {subjectNote ? (
                  <i className="ri-file-pdf-line align-bottom text-danger display-5 cursor-pointer"></i>
                ) : (
                  <i className="ri-file-upload-line align-bottom text-danger display-5 cursor-pointer"></i>
                )}
                {!subjectNote?.file && !uploadSubjectNoteResponse && (
                  <h6 className="fs-12 folder-name mt-1 cursor-pointer">
                    {t("clickToUpload")}
                  </h6>
                )}
                <h6 className="fs-12 text-mute cursor-pointer">
                  {typeof uploadError === "string" && (
                    <Label className="invalid-feedback d-block">
                      {uploadError}
                    </Label>
                  )}
                </h6>
                <div className=" text-muted">
                  {(subjectNote?.file || uploadSubjectNoteResponse?.path) && (
                    <React.Fragment>
                      <span className="me-auto text-center">
                        <b>{t("name")}</b>{" "}
                        {uploadSubjectNoteResponse?.name ||
                          subjectNote?.file.name}
                      </span>
                      <br />
                      <span className="me-auto">
                        <b>{t("size")}</b>{" "}
                        {formatFileSize(
                          Number(uploadSubjectNoteResponse?.size) ||
                            Number(subjectNote?.file?.size) ||
                            0
                        )}
                      </span>
                    </React.Fragment>
                  )}
                  <br />
                  <span>{uploading && <Spinner size={"sm"} />}</span>
                </div>
              </label>
              <br />
              {(subjectNote?.file?.path || uploadSubjectNoteResponse?.path) &&
                !uploading && (
                  <div>
                    {downloading ? (
                      <span className="me-auto">
                        <Spinner size={"sm"} /> {t("downloading")}
                      </span>
                    ) : (
                      <Button
                        onClick={onDownload}
                        className="btn btn-sm btn-primary"
                      >
                        {t("download")}
                      </Button>
                    )}
                  </div>
                )}
              <Input
                id="subject-note-input"
                type="file"
                className="profile-foreground-img-file-input d-none"
                accept={PDF_ACCEPT}
                onChange={onChangeFile}
                disabled={uploading}
              />
            </div>
            <SequenceNumberInput
              tabIndex={1}
              value={form.values.sequenceNumber}
              onChange={(e) => {
                e.preventDefault();
                const { value } = e.target;
                const regex =
                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                if (regex.test(value.toString())) {
                  form.setFieldValue("sequenceNumber", value);
                } else {
                  form.setFieldValue("sequenceNumber", "");
                }
              }}
              onBlur={form.handleBlur}
              invalid={
                form.touched.sequenceNumber && form.errors.sequenceNumber
                  ? true
                  : false
              }
              sequenceType={SequenceEnum.SUBJECT_NOTE}
              onGenerateSequenceNumber={(v: number) => {
                form.setFieldValue("sequenceNumber", v);
              }}
              mode={id ? "edit" : "add"}
              request={{
                type: SequenceEnum.SUBJECT_NOTE,
                subjectId: subjectId,
              }}
            />
            {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
              <FormFeedback type="invalid">
                {form.errors.sequenceNumber as string}
              </FormFeedback>
            ) : null}
            <div className="mb-3">
              <label htmlFor="name.en" className="form-label">
                {t("nameEn")}
                <span className="text-danger">*</span>
              </label>
              <Input
                disabled={getSubjectNoteError}
                autoComplete="off"
                id="name.en"
                name="name.en"
                value={form.values?.name.en}
                type="text"
                className="form-control"
                placeholder={t("enterInEnglish")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.en && form.errors.name?.en ? true : false
                }
              />
              {form.touched.name?.en && form.errors.name?.en ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.en as string}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="name.ar" className="form-label">
                {t("nameAr")}
                <span className="text-danger">*</span>
              </label>
              <Input
                style={{ fontFamily: "aljannat", direction: "rtl" }}
                disabled={getSubjectNoteError}
                autoComplete="off"
                auto
                id="name.ar"
                name="name.ar"
                value={form.values.name?.ar}
                type="text"
                className="form-control"
                placeholder={t("enterInArabic")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                invalid={
                  form.touched.name?.ar && form.errors.name?.ar ? true : false
                }
              />
              {form.touched.name?.ar && form.errors.name?.ar ? (
                <FormFeedback type="invalid">
                  {form.errors.name?.ar as string}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={loading || updateSubjectNoteLoading}
              disabled={
                loading ||
                updateSubjectNoteLoading ||
                getSubjectNoteError ||
                uploading
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
